import React, { useState } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { Text, TextInput, Button, Card } from 'react-native-paper';
import Icon from 'react-native-vector-icons/FontAwesome';
import { pickPhotos, toFormFile, type PickedPhoto } from '../Common/photoPicker';
import { useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import { useGetAmCategoriesQuery } from '../redux/services/amcategoryApi';
import { useGetDepartmentsQuery } from '../redux/services/emmdepartmentApi';
import { useInsertAmAssetMutation, useLazyGetAmNextCodeQuery } from '../redux/services/amassetApi';
import { useUploadAmDocumentMutation } from '../redux/services/amdocumentApi';
import AmPhoto from '../Components/AmPhoto';
import ProductPicker from '../Components/ProductPicker';
import type { AmProduct } from '../redux/services/amproductApi';
import { ShowToast } from '../Common/common';

import T from '../Components/T';
type Photo = PickedPhoto;

/** Register a new asset directly (not via a purchase): quantity N creates CODE-1..CODE-N. */
const AmAssetEntry = ({ navigation }: any) => {
  const company = useSelector((state: RootState) => state.gdata?.company);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [serial, setSerial] = useState('');
  const [description, setDescription] = useState('');
  const [cost, setCost] = useState('');
  const [product, setProduct] = useState<AmProduct | null>(null);
  const [qty, setQty] = useState('1');
  const [category, setCategory] = useState<number | null>(null);
  const [department, setDepartment] = useState<number | null>(null);
  const [open, setOpen] = useState<'category' | 'department' | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [saving, setSaving] = useState(false);

  const { data: categories = [] } = useGetAmCategoriesQuery(company || '', { skip: !company });
  const { data: departments = [] } = useGetDepartmentsQuery(company || '', { skip: !company });
  const [insertAsset] = useInsertAmAssetMutation();
  const [getNextCode] = useLazyGetAmNextCodeQuery();
  const [uploadDocument] = useUploadAmDocumentMutation();

  // Picking a product fills the same defaults as the web register (name/code/category only when still empty)
  const handleProduct = (p: AmProduct | null) => {
    setProduct(p);
    if (!p) return;
    if (!name.trim()) setName(p.pt_name);
    if (!code.trim()) {
      setCode(p.pt_code);
      // the product code is the default asset code; swap in the next free one when it is already used
      getNextCode({ code: p.pt_code })
        .unwrap()
        .then(r => {
          if (r.changed) setCode(cur => (cur === p.pt_code ? r.code : cur));
        })
        .catch(() => {});
    }
    if (p.pt_category) setCategory(Number(p.pt_category));
    if (p.pt_cost != null && p.pt_cost > 0) setCost(String(p.pt_cost));
    if (!description.trim() && (p.pt_brand || p.pt_model)) {
      setDescription(`${p.pt_brand || ''} ${p.pt_model || ''}`.trim());
    }
  };

  const addPhotos = async (source: 'camera' | 'library') => {
    try {
      const picked = await pickPhotos(source);
      if (picked.length) setPhotos(prev => [...prev, ...picked]);
    } catch (e: any) {
      ShowToast(e?.message || 'Could not open the camera', 'error');
    }
  };
  const takePhoto = () => addPhotos('camera');
  const pickPhoto = () => addPhotos('library');

  const handleSave = async () => {
    if (!name.trim() || !code.trim()) {
      ShowToast('Asset name and code are required', 'error');
      return;
    }
    const count = Math.min(50, Math.max(1, parseInt(qty, 10) || 1));
    setSaving(true);
    try {
      const res = await insertAsset({
        as_name: name.trim(),
        as_code: code.trim(),
        as_serialno: serial.trim() || undefined,
        as_product: product ? Number(product.pt_id) : undefined,
        as_notes: description.trim() || undefined,
        as_purchasecost: cost ? parseFloat(cost) || undefined : undefined,
        as_depmethod: product?.pt_depmethod || 'S',
        as_deprate: product?.pt_deprate ?? undefined,
        as_usefullife: product?.pt_usefullife ?? undefined,
        as_salvagevalue: product?.pt_salvagevalue ?? 0,
        as_category: category ?? undefined,
        as_department: department ?? undefined,
        as_status: 'Active',
        as_active: '1',
        qty: count
      } as any).unwrap();

      const ids = res.as_ids && res.as_ids.length ? res.as_ids : [res.as_id];
      let failed = 0;
      for (const id of ids) {
        for (const p of photos) {
          const fd = new FormData();
          fd.append('file', toFormFile(p));
          fd.append('as_asset', String(id));
          fd.append('ed_reftype', 'Asset');
          fd.append('category', 'Photo');
          try {
            await uploadDocument(fd).unwrap();
          } catch (e) {
            failed++;
          }
        }
      }
      if (failed) {
        ShowToast(`Saved, but ${failed} photo(s) failed to upload`, 'error');
      } else {
        ShowToast(count > 1 ? `${count} assets created` : 'Asset created');
      }
      navigation.goBack();
    } catch (err: any) {
      ShowToast(err?.data?.message || 'Failed to save asset', 'error');
    } finally {
      setSaving(false);
    }
  };

  const catName = categories.find(c => Number(c.ct_id) === category)?.ct_name;
  const deptName = departments.find(d => Number(d.dp_id) === department)?.dp_name;

  return (
    <ScrollView style={styles.container}>
      <Card style={styles.card}>
        <ProductPicker value={product ? Number(product.pt_id) : null} onSelect={handleProduct} />
        <TextInput label="Asset Name *" value={name} onChangeText={setName} mode="outlined" style={styles.input} />
        <TextInput label="Asset Code *" value={code} onChangeText={setCode} mode="outlined" autoCapitalize="characters" style={styles.input} />
        <TextInput label="Serial No." value={serial} onChangeText={setSerial} mode="outlined" style={styles.input} />
        <TextInput label="Purchase cost (per unit)" value={cost} onChangeText={setCost} keyboardType="decimal-pad" mode="outlined" style={styles.input} />
        <TextInput
          label="Description"
          value={description}
          onChangeText={setDescription}
          mode="outlined"
          multiline
          numberOfLines={3}
          style={styles.input}
        />
        <TextInput
          label="Quantity"
          value={qty}
          onChangeText={setQty}
          mode="outlined"
          keyboardType="numeric"
          style={styles.input}
        />
        {(parseInt(qty, 10) || 1) > 1 ? (
          <Text style={styles.hint}>
            {serial.trim()
              ? `Creates ${parseInt(qty, 10)} assets: ${code || 'CODE'}-1 to ${code || 'CODE'}-${parseInt(qty, 10)}`
              : `No serial number: one record with quantity ${parseInt(qty, 10)}`}
          </Text>
        ) : null}

        <TouchableOpacity style={styles.select} onPress={() => setOpen(open === 'category' ? null : 'category')}>
          <Text>{catName || 'Category (optional)'}</Text>
        </TouchableOpacity>
        {open === 'category' && (
          <ScrollView style={styles.dropdown} nestedScrollEnabled>
            <TouchableOpacity style={styles.dropdownItem} onPress={() => { setCategory(null); setOpen(null); }}>
              <Text><T>None</T></Text>
            </TouchableOpacity>
            {categories.map(c => (
              <TouchableOpacity key={String(c.ct_id)} style={styles.dropdownItem} onPress={() => { setCategory(Number(c.ct_id)); setOpen(null); }}>
                <Text>{c.ct_name}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        )}

        <TouchableOpacity style={styles.select} onPress={() => setOpen(open === 'department' ? null : 'department')}>
          <Text>{deptName || 'Department (optional)'}</Text>
        </TouchableOpacity>
        {open === 'department' && (
          <ScrollView style={styles.dropdown} nestedScrollEnabled>
            <TouchableOpacity style={styles.dropdownItem} onPress={() => { setDepartment(null); setOpen(null); }}>
              <Text><T>None</T></Text>
            </TouchableOpacity>
            {departments.map(d => (
              <TouchableOpacity key={String(d.dp_id)} style={styles.dropdownItem} onPress={() => { setDepartment(Number(d.dp_id)); setOpen(null); }}>
                <Text>{d.dp_name}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        )}

        <View style={styles.photoRow}>
          {photos.map((p, i) => (
            <AmPhoto key={`${p.uri}-${i}`} uri={p.uri} onRemove={() => setPhotos(prev => prev.filter((_, k) => k !== i))} />
          ))}
          <TouchableOpacity style={styles.photoBtn} onPress={takePhoto}>
            <Icon name="camera" size={18} color="#2E7D32" />
          </TouchableOpacity>
          <TouchableOpacity style={styles.photoBtn} onPress={pickPhoto}>
            <Icon name="image" size={18} color="#2E7D32" />
          </TouchableOpacity>
        </View>
      </Card>

      <View style={styles.buttons}>
        <Button mode="contained" onPress={handleSave} loading={saving} disabled={saving}>
          Save Asset
        </Button>
        <Button mode="outlined" onPress={() => navigation.goBack()} disabled={saving}>
          Cancel
        </Button>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5', padding: 10 },
  card: { padding: 14, marginBottom: 12 },
  input: { marginBottom: 10 },
  hint: { fontSize: 12, color: '#666', marginBottom: 10 },
  select: { borderWidth: 1, borderColor: '#ddd', borderRadius: 4, padding: 12, backgroundColor: '#fafafa', marginBottom: 10 },
  dropdown: { maxHeight: 180, borderWidth: 1, borderColor: '#ddd', borderRadius: 4, backgroundColor: '#fff', marginTop: -6, marginBottom: 10 },
  dropdownItem: { padding: 12, borderBottomWidth: 1, borderBottomColor: '#eee' },
  photoRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 4 },
  photoBtn: { width: 64, height: 64, borderRadius: 6, borderWidth: 1, borderStyle: 'dashed', borderColor: '#2E7D32', alignItems: 'center', justifyContent: 'center' },
  buttons: { gap: 10, marginBottom: 30 }
});

export default AmAssetEntry;
